import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import AdminAuditTable from "./AdminAuditTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminAuditPanel = () => {
  const [search, setSearch] = useState("");
  const [admin, setAdmin] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedAdmin = useDebouncedValue(admin, 300);

  const predicates: PropertyPredicateDto[] = [];
  if (debouncedSearch.trim().length > 0) predicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
  if (debouncedAdmin.trim().length > 0) predicates.push({ propertyName: "admin", propertyValue: debouncedAdmin.trim(), operation: "CONTAINS" });

  const auditQuery = useQuery({
    queryKey: ["admin-audit", { page, pageSize, debouncedSearch, debouncedAdmin }],
    queryFn: () => adminApi.listAudit({
      predicates,
      page: {
        number: page,
        size: pageSize,
        numberOfElements: null,
        hasNext: false,
        hasPrevious: false,
        first: false,
        last: false,
        sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
      },
    }),
    placeholderData: (previousData) => previousData,
  });

  const error = auditQuery.error ? apiErrorToMessage(auditQuery.error, "Failed to load audit history.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminAuditTable
        entries={auditQuery.data?.entries ?? []}
        isLoading={auditQuery.isLoading}
        search={search}
        admin={admin}
        pageSize={pageSize}
        page={page}
        resultsCount={auditQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={auditQuery.data?.page.hasNext ?? false}
        hasPreviousPage={auditQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onAdminChange={(value) => {
          setAdmin(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminAuditPanel;
